import {
  Filters,
  useGetPlanSummariesQuery,
  getStatusFilterTitle,
  getResultFilterTitle,
  useGetMatricesQuery,
  useGetProductsQuery,
  useGetSampntTypes,
  useGetSubstancesQuery,
  Mtds,
  type PlanSummary,
  getProducerSearchChipLabel
} from 'prove-common'

type FilterTagsProps<T extends string> = {
  filters: Filters<T>
  setFilters: React.Dispatch<React.SetStateAction<Filters<T>>>
  isFiltering: boolean
}

function formatDate(dateString: string) {
  if (!dateString) return ''
  const date = new Date(dateString)
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${dd}.${mm}.${yyyy}`
}

type PlanChipsProps<T extends string> = {
  subPlanIds: string[] | undefined
  planIds: string[] | undefined
  plans: PlanSummary[] | undefined
  isPlansPending: boolean
  filters: Filters<T>
  setFilters: React.Dispatch<React.SetStateAction<Filters<T>>>
}

function PlanChips<T extends string>({
  subPlanIds,
  planIds,
  plans,
  isPlansPending,
  filters,
  setFilters
}: Readonly<PlanChipsProps<T>>) {
  if (Array.isArray(subPlanIds) && subPlanIds.length > 0 && plans) {
    return (
      <>
        {subPlanIds.map((subId) => {
          const label =
            plans.find((p) => `${p.id}` === subId)?.name ?? `Plan ID: ${subId}`
          return (
            <Mtds.Chip
              key={`sub-${subId}`}
              data-removable={!isPlansPending}
              onClick={() =>
                setFilters({
                  ...filters,
                  subPlanIds: subPlanIds.filter((id) => id !== subId)
                })
              }
            >
              <span className='max-w-64 truncate'>
                {isPlansPending ? <Mtds.Skeleton className='w-8' /> : label}
              </span>
            </Mtds.Chip>
          )
        })}
      </>
    )
  }
  return (
    <>
      {planIds?.map((planId) => {
        const label =
          plans?.find(({ id }) => `${id}` === planId)?.name ??
          `Plan ID: ${planId}`
        return (
          <Mtds.Chip
            key={planId}
            data-removable={!isPlansPending}
            onClick={() =>
              setFilters({
                ...filters,
                planIds: planIds?.filter((id) => id !== planId)
              })
            }
          >
            <span className='max-w-64 truncate'>
              {isPlansPending ? <Mtds.Skeleton className='w-8' /> : label}
            </span>
          </Mtds.Chip>
        )
      })}
    </>
  )
}

export const FilterTags = <T extends string>({
  filters,
  setFilters,
  isFiltering
}: FilterTagsProps<T>) => {
  const {
    planIds,
    subPlanIds,
    productIds,
    matrixIds,
    sampntTypeIds,
    substanceIds,
    sampleId,
    producerSearch,
    status,
    result
  } = filters
  const { data: plans, isPending: isPlansPending } = useGetPlanSummariesQuery(
    !!planIds?.length || !!subPlanIds?.length
  )
  const { data: products, isPending: isProductsPending } = useGetProductsQuery(
    !!productIds?.length
  )
  const { data: matrices, isPending: isMatricesPending } = useGetMatricesQuery(
    !!matrixIds?.length
  )
  const { data: sampntTypes, isPending: isSampntTypesPending } =
    useGetSampntTypes(!!sampntTypeIds?.length)
  const { data: substances, isPending: isSubstancesPending } =
    useGetSubstancesQuery(!!substanceIds?.length)

  return isFiltering ? (
    <Mtds.Flex>
      {sampleId != null && (
        <Mtds.Chip
          data-removable
          onClick={() => setFilters({ ...filters, sampleId: undefined })}
        >
          # {sampleId}
        </Mtds.Chip>
      )}

      {status?.map((statusId) => (
        <Mtds.Chip
          key={statusId}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              status: status?.filter((id) => id !== statusId)
            })
          }
        >
          {getStatusFilterTitle(statusId)}
        </Mtds.Chip>
      ))}

      {!!result && (
        <Mtds.Chip
          data-removable
          onClick={() => {
            setFilters({
              ...filters,
              result: undefined
            })
          }}
        >
          {getResultFilterTitle(result)}
        </Mtds.Chip>
      )}

      {producerSearch?.trim() && (
        <Mtds.Chip
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              producerSearch: undefined
            })
          }
        >
          {getProducerSearchChipLabel(producerSearch)}: {producerSearch.trim()}
        </Mtds.Chip>
      )}

      <PlanChips
        subPlanIds={subPlanIds}
        planIds={planIds}
        plans={plans}
        isPlansPending={isPlansPending}
        filters={filters}
        setFilters={setFilters}
      />

      {productIds?.map((productId) => {
        const label =
          products?.find(({ id }) => `${id}` === productId)?.name ??
          `Produkt ID: ${productId}`
        return (
          <Mtds.Chip
            key={productId}
            data-removable={!isProductsPending}
            onClick={() =>
              setFilters({
                ...filters,
                productIds: productIds?.filter((id) => id !== productId)
              })
            }
          >
            {isProductsPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}

      {matrixIds?.map((matrixId) => {
        const label =
          matrices?.find(({ id }) => `${id}` === matrixId)?.name ??
          `Matrix ID: ${matrixId}`
        return (
          <Mtds.Chip
            key={matrixId}
            data-removable={!isMatricesPending}
            onClick={() =>
              setFilters({
                ...filters,
                matrixIds: matrixIds?.filter((id) => id !== matrixId)
              })
            }
          >
            {isMatricesPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}

      {substanceIds?.map((substanceId) => {
        const label =
          substances?.find(({ id }) => `${id}` === substanceId)?.name ??
          `Substans ID: ${substanceId}`
        return (
          <Mtds.Chip
            key={substanceId}
            data-removable={!isSubstancesPending}
            onClick={() =>
              setFilters({
                ...filters,
                substanceIds: substanceIds?.filter((id) => id !== substanceId)
              })
            }
          >
            {isSubstancesPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}
      {filters.fromDate && (
        <Mtds.Chip
          key={'fromDate'}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              fromDate: undefined
            })
          }
        >
          Fra: {formatDate(filters.fromDate)}
        </Mtds.Chip>
      )}
      {filters.toDate && (
        <Mtds.Chip
          key={'toDate'}
          data-removable
          onClick={() =>
            setFilters({
              ...filters,
              toDate: undefined
            })
          }
        >
          Til: {formatDate(filters.toDate)}
        </Mtds.Chip>
      )}
      {sampntTypeIds?.map((sampntTypeId) => {
        const label =
          sampntTypes?.find(({ id }) => `${id}` === sampntTypeId)?.nameNo ??
          `Uttakssted ID: ${sampntTypeId}`
        return (
          <Mtds.Chip
            key={sampntTypeId}
            data-removable={!isSampntTypesPending}
            onClick={() =>
              setFilters({
                ...filters,
                sampntTypeIds: sampntTypeIds?.filter(
                  (id) => id !== sampntTypeId
                )
              })
            }
          >
            {isSampntTypesPending ? <Mtds.Skeleton className='w-8' /> : label}
          </Mtds.Chip>
        )
      })}
    </Mtds.Flex>
  ) : null
}
